import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  AuthService,
  CmsComponent,
  CmsNavigationComponent,
  CmsService,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  User
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, distinctUntilChanged, filter, map, switchMap, take, tap, } from 'rxjs/operators';
import { CmsComponentData, NavigationNode, NavigationService, PageLayoutService, } from '@spartacus/storefront';
import { ActiveCartFacade, MultiCartFacade } from '@spartacus/cart/base/root';
import { CheckoutQueryResetEvent } from '@spartacus/checkout/base/root';
import { GenLoadingEnum } from "../../../enums/loading.enum";
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GenLoadingSpinnerService } from 'src/app/generac/services/loading-spinner.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';

@Component({
  selector: 'gen-login',
  templateUrl: './login.component.html',
  standalone: false
})
export class GenLoginComponent implements OnInit {
  data$: Observable<CmsNavigationComponent>;
  node$: BehaviorSubject<NavigationNode>;
  user: User | undefined;
  isCheckoutPage$: Observable<boolean>;
  loading = GenLoadingEnum.Idle;
  GenLoadingEnum = GenLoadingEnum;

  constructor(
    private auth: AuthService,
    private userAccount: UserAccountFacade,
    private cd: ChangeDetectorRef,
    protected componentData: CmsComponentData<CmsNavigationComponent>,
    protected service: NavigationService,
    protected cmsService: CmsService,
    protected customerService: GenCustomerService,
    protected multiCartService: MultiCartFacade,
    protected genLoadingSpinnerService: GenLoadingSpinnerService,
    protected globalMessageService: GlobalMessageService,
    protected activeCartFacade: ActiveCartFacade,
    protected pageLayoutService: PageLayoutService,
    protected routingService: RoutingService,
    protected eventService: EventService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
  ) { }

  ngOnInit(): void {
    this.data$ = this.componentData.data$.pipe(
      map((val) => ({ ...val, resetMenuOnClose: true }))
    );
    this.node$ = new BehaviorSubject<NavigationNode>(undefined);
    this.getCurrentUser()
      .pipe(
        filter((user) => !!user),
        tap((user) => {
          this.user = user;
          this.loading = GenLoadingEnum.Loaded;
        }),
        switchMap((user) =>
          combineLatest([
            this.getB2BUnitDataNavNode(user),
            this.getHeaderLinksNavNode(user),
          ])
        ),
        map((navNodes) => ({ children: navNodes }))
      )
      .subscribe((val) => {
        this.node$.next(val);
        this.cd.markForCheck();
      });
    this.isCheckoutPage$ = this.pageLayoutService.page$.pipe(
      take(1),
      map((page) => page.template === 'MultiStepCheckoutSummaryPageTemplate')
    );
  }

  private getHeaderLinksNavNode(
    user: User
  ): Observable<NavigationNode> {
    const headerLinksComp: Observable<CmsComponent> = this.cmsService
      .getContentSlot('HeaderLinks')
      .pipe(
        switchMap((value) =>
          this.cmsService.getComponentData(value.components[0].uid)
        )
      );

    return this.service.getNavigationNode(headerLinksComp).pipe(
      map((node) => ({
        ...node,
        children: node.children.filter((ch) => !!ch.title && !!ch.url),
      })),
      map((node) => ({
        ...node,
        header: 'Account',
        title: this.normalizeStr(user.name, 15),
      }))
    );
  }

  private getB2BUnitDataNavNode(
    user: User
  ): Observable<NavigationNode> {
    const b2bNavNode: Observable<NavigationNode> = this.customerService
      .getB2BUnits()
      .pipe(
        filter((units) => units?.length > 0),
        map((units) => ({
          children: units.map((val) => ({
            title: `${val.partnerId || ''} - ${this.normalizeStr(
              val.name || val.uid,
              40
            )}${val.town ? `, ${val.town}` : ''}`,
            uid: val.uid,
            desiredShipmentDateAvailable: val.desiredShipmentDateAvailable,
            customClickHandler: (event: Event) => {
              this.genLoadingSpinnerService.showSpinner();

              const setB2BUnit$ = this.customerService
                .setB2BUnit(val.partnerId, user.uid)
                .pipe(
                  take(1),
                  catchError((error) => {
                    this.genLoadingSpinnerService.hideSpinner();
                    this.globalMessageService.add(
                      { key: 'httpHandlers.unknownError' },
                      GlobalMessageType.MSG_TYPE_ERROR
                    );
                    return throwError(() => error);
                  })
                );

              combineLatest([setB2BUnit$, this.isCheckoutPage$]).subscribe(
                ([b2bUnit, isCheckoutPage]) => {
                  const { name, uid } = val;
                  const newVal: any = { ...this.node$.value };
                  newVal.children[0].title = this.normalizeStr(name || uid);
                  this.setCurrentActiveBusinessUnit(
                    newVal.children[0],
                    val.uid
                  );
                  this.node$.next(newVal);
                  this.multiCartService.loadCart({
                    userId: user.uid,
                    cartId: b2bUnit.code,
                    extraData: { active: true },
                  });
                  this.eventService.dispatch({}, CheckoutQueryResetEvent);
                  if (isCheckoutPage) {
                    this.routingService.go({ cxRoute: 'cart' });
                  }
                  this.genLoadingSpinnerService.hideSpinner();
                  this.globalMessageService.add(
                    { key: 'httpHandlers.b2bUnitChanged' },
                    GlobalMessageType.MSG_TYPE_CONFIRMATION
                  );
                }
              );
            },
          })),
        }))
      );

    return b2bNavNode.pipe(
      filter((b2bNavNode) => b2bNavNode?.children?.length > 0),
      map((navNode) => {
        let matchingNode = this.setCurrentActiveBusinessUnit(
          navNode,
          user.orgUnit?.uid
        );
        localStorage.setItem('desiredShipmentDateAvailable', JSON.stringify(matchingNode.desiredShipmentDateAvailable));
        return {
          ...navNode,
          header: 'Company',
          title:
            (!!matchingNode && this.normalizeStr(user.orgUnit?.name)) ||
            'My Company',
        };
      }),
      defaultIfEmpty({})
    );
  }

  private getCurrentUser(): Observable<User | undefined> {
    return this.auth.isUserLoggedIn().pipe(
      distinctUntilChanged(),
      tap(isLoggedIn => this.loading = isLoggedIn ? GenLoadingEnum.Loading : GenLoadingEnum.Loaded),
      filter((value) => !!value),
      switchMap(() => this.userAccount.get()),
      tap((user) => {
        if(user?.roles) {
          if(user?.roles) {
          this.googleAnalyticsService.sendUserType(user);
        }
        }
      })
    );
  }

  private normalizeStr(str: string, cutAt = 20): string | undefined {
    const capitalizedStr =
      str && str[0].toUpperCase() + str.slice(1).toLowerCase();

    if (capitalizedStr?.length > cutAt) {
      return capitalizedStr.substring(0, cutAt) + '...';
    }
    return capitalizedStr;
  }

  private setCurrentActiveBusinessUnit(
    rootB2BNode: NavigationNode,
    uid?: String
  ): NavigationNode {
    rootB2BNode.children.forEach((node) => (node.isActive = false));
    let matchingNode: NavigationNode | undefined =
      rootB2BNode.children.find((value) => value.uid === uid);
    if (matchingNode) {
      matchingNode.isActive = true;
    }

    return matchingNode;
  }
}
